/**
 * "Whose usage am I looking at?" — the one control shared by all three usage
 * panels (AI tokens, Network requests, API calls).
 *
 * ADMIN ONLY, and it renders NOTHING for anyone else. That is not a cosmetic
 * choice: the backend refuses a non-admin who names another user rather than
 * quietly answering with their own numbers (see jcseo/api/usage_common.py), so a
 * control that appeared for them could only ever produce an error.
 *
 * The value is the raw `user_id` query param the API takes — `''` for your own
 * usage, `'all'` for everyone, or a user id as a string. Keeping it in the
 * caller's state means each panel decides what to reset when the scope changes
 * (a drill-down opened against the old scope is stale, and must be closed).
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import type { AdminUser } from '../types';

export const ALL_USERS = 'all';

/** Label a user the way an admin knows them, not by id. */
export function userLabel(user: AdminUser): string {
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.username || user.email;
}

export default function UsageUserFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [users, setUsers] = useState<AdminUser[] | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api
      .listUsers('approved')
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      // A failed list leaves the select with just "Mine" and "Everyone", which
      // still work — this is a convenience, not the panel.
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      title="Whose usage to show"
    >
      <option value="">My usage</option>
      <option value={ALL_USERS}>Everyone</option>
      {(users ?? [])
        .filter((u) => u.id !== user?.id)
        .map((u) => (
          <option key={u.id} value={String(u.id)}>
            {userLabel(u)}
          </option>
        ))}
    </select>
  );
}
